import type { FileMetadata } from '@magicschool/business-logic/tools';
import type { Language } from '@magicschool/supabase/types';
import { getLanguageSpecificGenerationWordLimit } from '@/util/wordLimits';

// Default word limit for file content
const DEFAULT_WORD_LIMIT = 100000;

/**
 * Processes files to ensure their content fits within a word limit
 * This prevents overwhelming the AI model with excessive file content
 *
 * @param files - Array of files to process
 * @param tokenLimit - Maximum total tokens allowed across all files (defaults to 100,000)
 * @param language - Language code for language-specific limits
 * @returns Processed files with potentially truncated content
 */
export function fitFilesInWordLimit(files: FileMetadata[], tokenLimit?: number, language?: Language): FileMetadata[] {
  if (!files || files.length === 0) {
    return [];
  }

  // Determine the effective token limit
  const effectiveTokenLimit = tokenLimit ?? (language ? getLanguageSpecificGenerationWordLimit(language) : DEFAULT_WORD_LIMIT);

  // Calculate total words across all files
  let totalWords = 0;
  const filesWithWordCount = files.map((file) => {
    const wordCount = file.numberOfWords || file.content?.split(/\s+/).length || 0;
    totalWords += wordCount;
    return { file, wordCount };
  });

  // If within limit, return original files
  if (totalWords <= effectiveTokenLimit) {
    return files;
  }

  // Calculate scaling factor for proportional truncation
  const scalingFactor = effectiveTokenLimit / totalWords;

  // Process files with proportional content truncation
  return filesWithWordCount.map(({ file, wordCount }) => {
    const allowedWords = Math.floor(wordCount * scalingFactor);

    if (!file.content || allowedWords >= wordCount) {
      return file;
    }

    // Truncate content to allowed word count
    const words = file.content.split(/\s+/);
    const truncatedContent = words.slice(0, allowedWords).join(' ');

    return {
      ...file,
      content: `${truncatedContent}... [content truncated due to length]`,
      numberOfWords: allowedWords,
    };
  });
}
